/**
 * A once-only gate for the quiet outlook refetch (B28, moved to shared/lib by C55): many cards can ask to read the
 * outlook again, and only the first asker on each outlook state (its dataUpdatedAt) is let through.
 *
 * @module refetchGate
 */

/** A shared once-only gate for the quiet outlook refetch (B28). */
export interface RefetchGate {
  /**
   * Asks to read the outlook again.
   *
   * @param key - What the outlook is for (postcode and unit).
   * @param stamp - When the outlook held now arrived (dataUpdatedAt).
   * @returns True for the first asker on this outlook state; false for every other card, a remounted fold, or the state a refetch itself produced.
   */
  claim: (key: string, stamp: number) => boolean;
  /**
   * Records the state a finished refetch left, so it is not read again for the same missing row.
   *
   * @param key - What the outlook is for.
   * @param stamp - dataUpdatedAt after the refetch.
   */
  settle: (key: string, stamp: number) => void;
}

/**
 * Makes a gate: one refetch per outlook state, however many cards ask.
 *
 * @returns A RefetchGate.
 */
export function createRefetchGate(): RefetchGate {
  /** Per key, the stamps already claimed or left by a refetch. */
  const seen = new Map<string, Set<number>>();
  return {
    claim(key, stamp) {
      const stamps = seen.get(key) ?? new Set<number>();
      seen.set(key, stamps);
      if (stamps.has(stamp)) return false;
      stamps.add(stamp);
      return true;
    },
    settle(key, stamp) {
      const stamps = seen.get(key) ?? new Set<number>();
      seen.set(key, stamps);
      stamps.add(stamp);
    },
  };
}
